import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Key, Globe, Loader2, PlugZap, CheckCircle2, AlertCircle, Headset } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const KEYS = [
  { key: "openrouter_api_key", label: "OpenRouter API key", hint: "Powers blog/SEO drafting, Google Ads optimiser, landing chat, merchant dashboard Support AI, sales WhatsApp AI, and domain-name AI suggestions. Get a key at openrouter.ai", placeholder: "sk-or-v1-..." },
  { key: "openrouter_model", label: "AI model", hint: "OpenRouter model id for every platform AI feature. Leave blank to use the flagship (anthropic/claude-opus-5). Legacy openai/gpt-4o is upgraded automatically.", placeholder: "anthropic/claude-opus-5" },
  { key: "resend_api_key", label: "Resend API key", hint: "For password reset and email verification emails.", placeholder: "re_..." },
  { key: "razorpay_platform_key_id", label: "Razorpay Platform Key ID", hint: "Platform-level Razorpay key (for OAuth partner integration).", placeholder: "rzp_live_..." },
  { key: "razorpay_platform_key_secret", label: "Razorpay Platform Key Secret", hint: "Kept encrypted at rest.", placeholder: "••••" },
  { key: "r2_endpoint", label: "Cloudflare R2 Endpoint", hint: "https://<accountid>.r2.cloudflarestorage.com", placeholder: "https://…r2.cloudflarestorage.com" },
  { key: "r2_bucket", label: "R2 Bucket", hint: "Bucket used for storefront images.", placeholder: "merastartup-media" },
  { key: "r2_access_key_id", label: "R2 Access Key ID", hint: "", placeholder: "" },
  { key: "r2_secret_access_key", label: "R2 Secret Access Key", hint: "", placeholder: "" },
  { key: "r2_public_base_url", label: "R2 Public Base URL", hint: "Public custom domain for objects in the bucket, e.g. https://media.merastartup.in (no trailing slash). Uploads are stored as {base}/{tenant-id}/{kind}/{uuid}.ext", placeholder: "https://media.merastartup.in" },
  { key: "codemagic_api_token", label: "Codemagic API token", hint: "User API token from Codemagic → Teams → Integrations. Used to start merchant iOS Flutter builds.", placeholder: "••••" },
  { key: "codemagic_app_id", label: "Codemagic app ID", hint: "The Codemagic application that builds the Flutter iOS template.", placeholder: "5b85cc73…" },
  { key: "codemagic_workflow_id", label: "Codemagic iOS workflow ID", hint: "Workflow that runs flutter build ipa. Default ios-release if blank.", placeholder: "ios-release" },
  { key: "codemagic_branch", label: "Codemagic git branch", hint: "Branch of the Flutter template repo. Default main.", placeholder: "main" },
];

const RC_KEYS = [
  { key: "resellerclub_auth_userid", label: "Reseller Club Auth User ID", hint: "Numeric Reseller ID from Settings → API in the Reseller Club panel.", placeholder: "123456" },
  { key: "resellerclub_api_key", label: "Reseller Club API key", hint: "Generate under Settings → API. Keep this secret. After save it stays stored — the box stays empty so we never echo the key back.", placeholder: "••••" },
  { key: "resellerclub_mode", label: "Mode (test or live)", hint: "Use test while developing against test.httpapi.com (OT&E account). Switch to live for real registrations.", placeholder: "live" },
  { key: "resellerclub_ns1", label: "Default nameserver 1", hint: "Passed at register because Reseller Club requires NS. Keep their defaults (ns1.onlyfordns.net). We do not overwrite NS after purchase.", placeholder: "ns1.onlyfordns.net" },
  { key: "resellerclub_ns2", label: "Default nameserver 2", hint: "Second nameserver. Example: ns2.onlyfordns.net", placeholder: "ns2.onlyfordns.net" },
  { key: "resellerclub_markup_percent", label: "Domain markup %", hint: "Retail = Reseller Club cost × (1 + this percent) + flat INR. Example 20 means 20% on top of registrar cost. Applies separately to first-year register and to renewal.", placeholder: "20" },
  { key: "resellerclub_markup_flat", label: "Domain markup flat (INR)", hint: "Added after the percent markup, per year. 0 = percent only.", placeholder: "0" },
  { key: "resellerclub_inr_per_usd", label: "INR per USD (optional)", hint: "Only if premium-check returns USD. Leave 0 when Reseller Club already bills INR.", placeholder: "0" },
  { key: "platform_store_ip", label: "Legacy store A-record IP", hint: "Used only to verify old custom domains that still use A records. New registrations CNAME to shops.merastartup.in instead.", placeholder: "187.127.153.67" },
];

const ESTIMATE_TLDS = [
  { tld: "com", hint: "Shown on /domain for .com until live markup returns" },
  { tld: "in", hint: "India ccTLD first-year estimate (INR)" },
  { tld: "co.in", hint: "Usually the cheapest listed extension" },
  { tld: "store", hint: "" },
  { tld: "shop", hint: "" },
  { tld: "online", hint: "" },
  { tld: "co", hint: "" },
  { tld: "net", hint: "" },
];

function estimateKey(tld) {
  return `resellerclub_estimate_${String(tld || "").replace(/\./g, "_")}`;
}

const SECRET_KEYS = new Set([
  "openrouter_api_key", "resend_api_key", "razorpay_platform_key_secret",
  "r2_access_key_id", "r2_secret_access_key", "resellerclub_api_key",
  "codemagic_api_token",
]);

const rupee = (n) => n == null || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;

export default function SuperIntegrations() {
  const [values, setValues] = useState({});
  const [existing, setExisting] = useState({});
  const [saved, setSaved] = useState({});
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [supportBusy, setSupportBusy] = useState(false);
  const supportOn = String(values.merchant_support_widget ?? existing.merchant_support_widget ?? "on").trim().toLowerCase() !== "off";

  const toggleMerchantSupport = async (on) => {
    const value = on ? "on" : "off";
    setValues((prev) => ({ ...prev, merchant_support_widget: value }));
    setSupportBusy(true);
    try {
      await api.put("/platform/settings", { key: "merchant_support_widget", value });
      toast.success(on ? "Mera Startup Support is on for every store" : "Mera Startup Support is off for every store");
      await load();
    } catch (e) {
      toast.error(extractErr(e));
      await load();
    } finally {
      setSupportBusy(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/platform/settings");
    const m = {};
    const s = {};
    for (const d of data || []) {
      m[d.key] = d.value_masked;
      s[d.key] = !!d.saved;
    }
    setExisting(m);
    setSaved(s);
    setValues((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(m)) {
        if (SECRET_KEYS.has(k)) continue;
        if (prev[k] === undefined || prev[k] === "") next[k] = v ?? "";
      }
      return next;
    });
  };
  useEffect(() => { load().catch((e) => toast.error(extractErr(e))); }, []);

  const save = async (k) => {
    const typed = values[k];
    if (typed === undefined || typed === "") {
      if (SECRET_KEYS.has(k) && saved[k]) {
        toast.message("This secret is already saved. Type a new value only if you want to replace it.");
        return;
      }
      toast.error("Enter a value, then click Save.");
      return;
    }
    try {
      await api.put("/platform/settings", { key: k, value: String(typed) });
      toast.success("Saved — it will still be here after refresh.");
      if (SECRET_KEYS.has(k)) {
        setValues((prev) => ({ ...prev, [k]: "" }));
      }
      await load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const saveEstimates = async () => {
    const rows = ESTIMATE_TLDS.map((t) => estimateKey(t.tld)).filter((k) => String(values[k] ?? "").trim() !== "");
    if (!rows.length) {
      toast.error("Enter at least one estimated INR, then save.");
      return;
    }
    try {
      for (const k of rows) {
        await api.put("/platform/settings", { key: k, value: String(values[k]).trim() });
      }
      toast.success("Estimated /domain prices saved — they show on first paint.");
      await load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const testRc = async () => {
    setProbing(true);
    try {
      const { data } = await api.post("/platform/resellerclub/test");
      setProbe(data);
      if (data.ok) toast.success(data.mode === "test" ? "Test API is reachable" : "Live API is reachable");
      else toast.error(data.error || "Reseller Club did not accept these keys");
    } catch (e) {
      setProbe({ ok: false, error: extractErr(e) });
      toast.error(extractErr(e));
    } finally {
      setProbing(false);
    }
  };

  const field = (row, dark = true) => {
    const secret = SECRET_KEYS.has(row.key);
    const hasSaved = !!saved[row.key];
    const shown = secret ? (values[row.key] || "") : (values[row.key] ?? existing[row.key] ?? "");
    return (
      <Card key={row.key} className={dark ? "bg-slate-900 border-slate-800 p-5" : "p-5"}>
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${dark ? "bg-indigo-500/20 border border-indigo-400/40" : "bg-orange-50 border border-orange-200"}`}>
            {row.key.startsWith("reseller") || row.key.startsWith("platform_store") ? <Globe className={`w-4 h-4 ${dark ? "text-orange-300" : "text-[#FF6B00]"}`} /> : <Key className="w-4 h-4 text-indigo-300" />}
          </div>
          <div className="flex-1">
            <Label className={dark ? "text-slate-100" : ""}>{row.label}</Label>
            {row.hint && <p className={`text-xs mt-0.5 ${dark ? "text-slate-500" : "text-slate-500"}`}>{row.hint}</p>}
            {secret && hasSaved ? (
              <p className="text-xs text-emerald-600 mt-1 font-mono-nums">Saved on server ({existing[row.key] || "••••"}). Leave blank to keep it.</p>
            ) : !secret && (existing[row.key] != null && existing[row.key] !== "") ? (
              <p className="text-xs text-emerald-600 mt-1 font-mono-nums">Saved: {existing[row.key]}</p>
            ) : null}
            <div className="mt-3 flex gap-2">
              <Input
                placeholder={secret && hasSaved ? "Leave blank to keep the saved key" : row.placeholder}
                data-testid={`int-${row.key}`}
                type={secret ? "password" : "text"}
                autoComplete="off"
                className={dark ? "bg-slate-800 border-slate-700 text-white font-mono-nums" : "font-mono-nums"}
                value={shown}
                onChange={(e) => setValues({ ...values, [row.key]: e.target.value })}
              />
              <Button onClick={() => save(row.key)} data-testid={`int-save-${row.key}`} className={dark ? "bg-indigo-500 hover:bg-indigo-600" : "bg-[#FF6B00] hover:bg-[#e86000]"}>Save</Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold">Integrations</h1>
        <p className="text-slate-500 text-sm mt-1">Platform-level API keys. Stored encrypted at rest (V1 fallback). Public fields stay filled after refresh; secrets stay saved but are not shown in the box.</p>
      </div>
      <Card className="bg-slate-900 border-slate-800 p-5" data-testid="merchant-support-widget-switch">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center flex-shrink-0">
            <Headset className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-slate-100 font-medium">Mera Startup Support</div>
            <p className="text-xs text-slate-500 mt-0.5">The floating button at the bottom-right of every merchant dashboard. Off hides it for all stores. On shows it for all stores.</p>
          </div>
          <Switch
            checked={supportOn}
            disabled={supportBusy}
            onCheckedChange={toggleMerchantSupport}
            data-testid="merchant-support-widget-toggle"
            aria-label="Mera Startup Support"
          />
        </div>
      </Card>
      <div className="space-y-4">
        {KEYS.map((row) => field(row, true))}
      </div>

      <div>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Globe className="w-5 h-5" /> Reseller Club (domains)</h2>
        <p className="text-slate-500 text-sm mt-1">Powers /domain search, registration, DNS and nameservers. OpenRouter (above) is reused for AI name suggestions.</p>
      </div>
      <Card className="p-5 bg-slate-50 border-slate-200 text-sm text-slate-700 space-y-2" data-testid="resellerclub-setup-help">
        <p className="font-semibold text-slate-900">How to set this up</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>Log in at <span className="font-mono">https://manage.resellerclub.com</span> (or your white-label panel).</li>
          <li>Open <b>Settings → API</b>. Copy your numeric <b>Reseller ID</b> (Auth User ID) and generate an <b>API key</b>.</li>
          <li><b>Whitelist this server’s public IPv4</b> under API access. Reseller Club rejects calls from unknown IPs. If you use a NAT or load balancer, whitelist the egress IP.</li>
          <li>Fund the reseller wallet (INR). Registrations use <code>invoice-option=NoInvoice</code>, so they debit your prepaid balance — customers pay us via Cashfree, we register on their behalf.</li>
          <li>Start with mode <code>test</code> against the OT&amp;E account if you have one, then switch to <code>live</code>.</li>
          <li>Leave default nameservers as Reseller Club DNS (typically <code>ns1.onlyfordns.net</code> / <code>ns2.onlyfordns.net</code>). We do not replace them after registration. DNS editor still needs <code>/api/dns/activate</code>.</li>
          <li>After a successful registration we do <b>not</b> add A records. We CNAME <code>www</code> → <code>shops.merastartup.in</code> (same as merchant Settings → Domain). We try CNAME <code>@</code> to the same host; if the registry refuses apex CNAMEs we URL-forward the root to <code>https://www.thedomain</code>. Store custom domain is saved as <code>www.…</code>.</li>
          <li><b>Store A-record IP</b> is only for legacy custom-domain checks (merchants who already pointed A records). New bought domains should not use it.</li>
          <li>Retail price = Reseller Club cost + markup (percent and/or flat INR below). Registration and renewal are priced separately. Premium names always use Reseller Club’s premium create/renew — TLD JSON never overrides them.</li>
          <li>Set <b>estimated /domain prices</b> for each extension below. Those INR amounts show immediately on /domain; live cost+markup replaces them after the registrar APIs return.</li>
          <li>TLD JSON below is a <b>fallback</b> for standard names when the reseller-price API has no cost for that extension (and for unlisted TLDs), and when no estimate is set. Checkout re-checks availability and live price so we do not charge a stale quote.</li>
        </ol>
      </Card>

      <Card className="p-5 border-orange-200 bg-orange-50/60 space-y-3" data-testid="resellerclub-test-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-semibold text-slate-900 flex items-center gap-2"><PlugZap className="w-4 h-4" /> Test keys, markup &amp; mode</p>
            <p className="text-xs text-slate-600 mt-1">This calls Reseller Club with whatever is saved (not the unsaved box). If search on /domain already returns names, your User ID + API key are working.</p>
          </div>
          <Button type="button" disabled={probing} onClick={testRc} data-testid="resellerclub-test-btn" className="bg-[#FF6B00] hover:bg-[#e86000]">
            {probing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PlugZap className="w-4 h-4 mr-1" />}
            Test connection
          </Button>
        </div>
        {probe ? (
          <div className={`rounded-xl border p-3 text-sm ${probe.ok ? "bg-white border-emerald-200" : "bg-white border-red-200"}`} data-testid="resellerclub-test-result">
            <div className="flex items-center gap-2 font-semibold">
              {probe.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
              {probe.ok ? "Connected" : "Not connected"}
              {probe.mode ? <span className="text-xs font-normal uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100">{probe.mode}</span> : null}
            </div>
            <ul className="mt-2 text-xs text-slate-600 space-y-1">
              <li>API host: <span className="font-mono">{probe.api_host || "—"}</span></li>
              <li>Auth User ID: <span className="font-mono">{probe.auth_userid || "not saved"}</span> · API key: {probe.api_key_saved ? "saved" : "missing"}</li>
              <li>Markup: {probe.markup_percent ?? 0}% + {rupee(probe.markup_flat)} flat</li>
              {probe.price_endpoints ? (
                <>
                  <li>Slab API: <span className="font-mono break-all">{probe.price_endpoints.reseller_price}</span></li>
                  <li>Cost API: <span className="font-mono break-all">{probe.price_endpoints.reseller_cost}</span></li>
                </>
              ) : null}
              {(probe.samples || (probe.sample ? [probe.sample] : [])).map((s) => (
                <li key={s.tld} className="font-mono-nums">
                  .{s.tld} key={s.product_key || "—"} · raw={s.raw_addnewdomain ? JSON.stringify(s.raw_addnewdomain) : "none"}
                  {s.cost_register != null ? (
                    <> · {s.calculation || `${s.cost_register} + markup`} → retail {rupee(s.retail_register)}</>
                  ) : (
                    <> · JSON fallback {rupee(s.retail_register)} ({s.fallback_reason || s.source})</>
                  )}
                </li>
              ))}
              {probe.error ? <li className="text-red-600">{probe.error}</li> : null}
            </ul>
          </div>
        ) : null}
        <div className="text-xs text-slate-600 space-y-1.5">
          <p className="font-semibold text-slate-800">How to run the full buying flow in test mode</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Create / use a Reseller Club <b>OT&amp;E (test)</b> account. Live keys against <code>test.httpapi.com</code> will fail.</li>
            <li>Save that test User ID + API key, set Mode to <code>test</code>, whitelist this server IP on the <b>test</b> panel, then click Test connection.</li>
            <li>Open <a className="underline" href="/domain">/domain</a> and search — availability comes from the test API. Prices use test reseller-price + your markup (or TLD JSON fallback).</li>
            <li>Checkout still uses Cashfree. Put Cashfree in <b>sandbox</b> so you are not charged live. Complete signup; the test API will not create a real public domain.</li>
            <li>When ready for customers: switch Mode to <code>live</code>, paste live keys, whitelist the live IP, Test connection, then try a cheap .co.in on /domain.</li>
          </ol>
        </div>
      </Card>

      <div className="space-y-4">
        {RC_KEYS.map((row) => field(row, false))}
        <Card className="p-5" data-testid="resellerclub-estimate-prices">
          <Label>Estimated /domain prices (INR, first year)</Label>
          <p className="text-xs text-slate-500 mt-0.5">
            These paint instantly when a customer opens /domain. After Reseller Club cost + your markup % returns (a few seconds), the page swaps to the live retail amount. Set numbers close to what markup will produce — e.g. if .in cost is ~₹635 and markup is 10%, estimate ₹699.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {ESTIMATE_TLDS.map((t) => {
              const k = estimateKey(t.tld);
              const shown = values[k] ?? existing[k] ?? "";
              return (
                <div key={k}>
                  <Label className="text-xs font-semibold">.{t.tld}</Label>
                  {existing[k] ? <p className="text-[11px] text-emerald-600 font-mono-nums">Saved: ₹{existing[k]}</p> : null}
                  <Input
                    className="mt-1 font-mono-nums"
                    inputMode="numeric"
                    placeholder="e.g. 699"
                    data-testid={`int-${k}`}
                    value={shown}
                    onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                  />
                </div>
              );
            })}
          </div>
          <Button className="mt-4 bg-[#FF6B00] hover:bg-[#e86000]" onClick={saveEstimates} data-testid="int-save-estimates">Save estimated prices</Button>
        </Card>
        <Card className="p-5">
          <Label>TLD fallback prices (JSON)</Label>
          <p className="text-xs text-slate-500 mt-0.5">Used only when Reseller Club has no standard cost for that TLD — never for premiums. Example: {`{"com": 999, "in": 599, "co.in": 499}`} or a list of {`{"tld","price","renew","tag","blurb"}`} objects.</p>
          {existing.resellerclub_tld_prices ? <p className="text-xs text-emerald-600 mt-1 font-mono-nums">Saved JSON is loaded in the box below.</p> : null}
          <Textarea
            className="mt-3 font-mono text-xs"
            rows={5}
            data-testid="int-resellerclub_tld_prices"
            placeholder='{"com": 999, "in": 599, "co.in": 499}'
            value={values.resellerclub_tld_prices || ""}
            onChange={(e) => setValues({ ...values, resellerclub_tld_prices: e.target.value })}
          />
          <Button className="mt-3 bg-[#FF6B00] hover:bg-[#e86000]" onClick={() => save("resellerclub_tld_prices")}>Save prices</Button>
        </Card>
      </div>
    </div>
  );
}
